import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ComputerLineIcon, DropboxIcon, Dropdown, Icon, message } from 'ui';
import type { ButtonProperties } from 'ui';
import { downloadDropboxFile } from '../../services';
import { DropboxChooser } from '../dropbox-chooser';

type DropboxFile = { link: string; name: string };

type P = {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
  onChange: (files: Array<Blob | File>) => void;
  size?: ButtonProperties['size'];
  type?: ButtonProperties['type'];
  uploading?: boolean;
};

const AttachmentFilePicker = ({ children, className, disabled, icon, onChange, size, type, uploading }: P) => {
  const { t } = useTranslation();
  const [openedKey, setOpenedKey] = useState<'dropbox'>();
  const [isLoading, setIsLoading] = useState(false);

  const fileInputReference = useRef<HTMLInputElement>(null);

  const onSelectDropbox = async (data: DropboxFile[]) => {
    setIsLoading(true);

    try {
      const promises = data.map(async (file) => {
        const response = await downloadDropboxFile({ url: file.link });
        return Object.assign(response.data, { name: file.name });
      });

      const files = await Promise.all(promises);
      onChange(files);
    } catch {
      message.error('Something went wrong');
    }

    setIsLoading(false);
  };

  const menu = [
    {
      icon: <ComputerLineIcon />,
      key: 'image-upload',
      label: t('Your Computer'),
      onClick() {
        fileInputReference.current?.click();
      },
    },
    {
      icon: <DropboxIcon />,
      key: 'dropbox',
      label: t('Dropbox'),
    },
  ];

  const onSelectKey = ({ key }: { key: string }) => {
    switch (key) {
      case 'dropbox':
        setOpenedKey('dropbox');
        break;
      case 'image-upload':
        break;
      default:
        throw new Error(t('Unsupported option: {{option}}', { option: key }));
    }
  };

  return (
    <>
      <Dropdown
        menu={{
          items: menu,
          onClick(event) {
            onSelectKey(event);
          },
        }}
        trigger={['click']}
      >
        <Button
          className={className}
          disabled={disabled}
          icon={icon ?? <Icon icon="attachment-line" />}
          loading={isLoading || uploading}
          size={size ?? 'large'}
          type={type ?? 'text'}
        >
          {children}
        </Button>
      </Dropdown>

      <input
        className="attachment-file-picker-input"
        multiple
        onChange={(event) => {
          const files = event.target.files;
          if (!files || files.length === 0) {
            return;
          }

          onChange([...files]);
          event.target.value = '';
        }}
        ref={fileInputReference}
        type="file"
      />

      <DropboxChooser
        onDropdownClose={() => {
          setOpenedKey(undefined);
        }}
        onSelect={onSelectDropbox}
        opened={openedKey === 'dropbox'}
      >
        <div className="attachment-file-picker-dropbox-chooser-trigger" />
      </DropboxChooser>
    </>
  );
};

export default AttachmentFilePicker;
